import React from "react";
import { X, Plus } from "lucide-react";
import { Character } from "./types";
import { StatCard } from "./StatCard";

interface InventoryTabProps {
	character: Character;
	updateCharacter: (path: string, value: any) => void;
	addItem: (category: keyof Character, item: any) => void;
	removeItem: (category: keyof Character, index: number) => void;
}

const handleEquipmentChange = (
	equipment: any[],
	index: number,
	field: string,
	value: any,
	updateCharacter: (path: string, value: any) => void,
) => {
	const newEquipment = equipment.map((item, i) =>
		i === index ? { ...item, [field]: value } : item,
	);
	updateCharacter("equipment", newEquipment);
};

export const InventoryTab: React.FC<InventoryTabProps> = ({
	character,
	updateCharacter,
	addItem,
	removeItem,
}) => (
	<div className="space-y-8">
		{/* Equipment */}
		<div className="grid grid-cols-1 gap-6">
			<StatCard title="Equipment">
				<button
					onClick={() =>
						addItem("equipment", { name: "", count: 1, weight: 0 })
					}
					className="flex items-center gap-2 px-4 py-2 mb-4 bg-indigo-500 text-white rounded-lg hover:bg-indigo-500 transition-colors duration-200 font-semibold text-sm"
				>
					<Plus size={16} />
					Add
				</button>
				<div className="grid grid-cols-2 gap-4">
					<div className="flex gap-2">
						<div className="w-6"></div>
						<div className="flex-1">
							<b>Name</b>
						</div>
						<div className="w-20">
							<b>Quantity</b>
						</div>
						<div className="w-20">
							<b>Weight</b>
						</div>
						<div className="w-8"></div>
					</div>
					<div className="flex gap-2">
						<div className="w-6"></div>
						<div className="flex-1">
							<b>Name</b>
						</div>
						<div className="w-20">
							<b>Quantity</b>
						</div>
						<div className="w-20">
							<b>Weight</b>
						</div>
						<div className="w-8"></div>
					</div>
				</div>
				<div className="grid grid-cols-2 gap-4">
					{character.equipment.map((item, index) => (
						<div
							key={index}
							draggable
							onDragStart={(e) => {
								e.dataTransfer.effectAllowed = "move";
								e.dataTransfer.setData(
									"text/plain",
									index.toString(),
								);
							}}
							onDragOver={(e) => {
								e.preventDefault();
								e.dataTransfer.dropEffect = "move";
							}}
							onDrop={(e) => {
								e.preventDefault();
								const fromIndex = parseInt(
									e.dataTransfer.getData("text/plain"),
								);
								const newEquipment = [...character.equipment];
								[newEquipment[fromIndex], newEquipment[index]] =
									[
										newEquipment[index],
										newEquipment[fromIndex],
									];
								updateCharacter("equipment", newEquipment);
							}}
							className="flex flex-col gap-2 group cursor-move hover:opacity-70 transition-opacity"
						>
							<div className="flex gap-2">
								<div className="w-6 flex items-center justify-center text-gray-400 group-hover:text-indigo-500 transition-colors">
									<svg
										className="w-5 h-5"
										fill="currentColor"
										viewBox="0 0 20 20"
									>
										<path d="M10 3a1.5 1.5 0 110 3 1.5 1.5 0 010-3zM10 8.5a1.5 1.5 0 110 3 1.5 1.5 0 010-3zM8.5 15.5a1.5 1.5 0 103 0 1.5 1.5 0 00-3 0z" />
									</svg>
								</div>
								<input
									type="text"
									value={item.name}
									onChange={(e) =>
										handleEquipmentChange(
											character.equipment,
											index,
											"name",
											e.target.value,
											updateCharacter,
										)
									}
									className="flex-1 px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-colors duration-200 text-sm"
									placeholder="Item"
								/>
								<input
									type="number"
									value={item.count}
									onChange={(e) =>
										handleEquipmentChange(
											character.equipment,
											index,
											"count",
											parseInt(e.target.value) || 0,
											updateCharacter,
										)
									}
									className="w-20 px-2 py-2 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-colors duration-200 text-sm"
									placeholder="Qty"
									min="0"
								/>
								<input
									type="number"
									value={item.weight}
									onChange={(e) =>
										handleEquipmentChange(
											character.equipment,
											index,
											"weight",
											parseFloat(e.target.value) || 0,
											updateCharacter,
										)
									}
									className="w-20 px-2 py-2 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-colors duration-200 text-sm"
									placeholder="Wt"
									min="0"
									step="0.1"
								/>
								<button
									onClick={() =>
										removeItem("equipment", index)
									}
									className="w-8 p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors duration-200"
								>
									<X size={16} />
								</button>
							</div>
						</div>
					))}
				</div>
			</StatCard>
		</div>

		{/* Money */}
		<StatCard title="Currency">
			<div className="grid grid-cols-2 md:grid-cols-5 gap-4">
				{[
					{
						name: "Copper",
						key: "copper" as const,
						color: "bg-orange-50 border-orange-300 text-orange-700",
						accent: "bg-orange-500",
					},
					{
						name: "Silver",
						key: "silver" as const,
						color: "bg-slate-50 border-slate-300 text-slate-700",
						accent: "bg-slate-500",
					},
					{
						name: "Electrum",
						key: "electrum" as const,
						color: "bg-amber-50 border-amber-300 text-amber-700",
						accent: "bg-amber-500",
					},
					{
						name: "Gold",
						key: "gold" as const,
						color: "bg-yellow-50 border-yellow-300 text-yellow-700",
						accent: "bg-yellow-500",
					},
					{
						name: "Platinum",
						key: "platinum" as const,
						color: "bg-blue-50 border-blue-300 text-blue-700",
						accent: "bg-blue-500",
					},
				].map((coin) => (
					<div key={coin.key} className="group">
						<label className="block text-xs font-semibold mb-3 text-gray-600 uppercase tracking-wide">
							{coin.name}
						</label>
						<div
							className={`${coin.color} border-2 rounded-2xl p-4 transition-transform duration-200 group-hover:shadow-lg group-hover:scale-105`}
						>
							<input
								type="number"
								value={character[coin.key]}
								onChange={(e) =>
									updateCharacter(
										coin.key,
										parseInt(e.target.value) || 0,
									)
								}
								className="w-full text-center text-2xl font-bold bg-transparent border-none outline-none"
								min="0"
							/>
						</div>
					</div>
				))}
			</div>
		</StatCard>

		{/* Other Treasure */}
		<StatCard title="Other Treasure">
			<textarea
				value={character.otherTreasure}
				onChange={(e) =>
					updateCharacter("otherTreasure", e.target.value)
				}
				className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-colors duration-200 resize-none"
				rows={6}
				placeholder="Gems, art objects, magic items, etc."
			/>
		</StatCard>
	</div>
);
